import { createServerFn } from "@tanstack/react-start";
import { eq, asc, and, inArray } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "../db/client.server";
import { requirePermission } from "../auth/session.server";
import { generateUniqueSlug } from "../utils/slug.server";
import { mediaIdSchema, resolvePortraitMediaInput } from "../media/input.server";
import {
  invalidateLeadersCache,
} from "./public.functions";

const leaderInput = z.object({
  name: z.string().min(1),
  slug: z.string().optional(),
  navLabel: z.string().min(1),
  designation: z.string().min(1),
  qualifications: z.string().optional(),
  memberships: z.array(z.string()).optional(),
  bio: z.array(z.string()).optional(),
  initials: z.string().optional(),
  portraitMediaId: mediaIdSchema,
  portraitUrl: z.string().optional().nullable(),
  sortOrder: z.number().optional(),
  status: z.enum(["draft", "published", "archived"]).default("published"),
});

export const listLeadersAdmin = createServerFn({ method: "GET" }).handler(async () => {
  await requirePermission("leaders", "read");
  return getDb()
    .select()
    .from(schema.leaders)
    .orderBy(asc(schema.leaders.sortOrder), asc(schema.leaders.name));
});

export const getLeaderAdmin = createServerFn({ method: "GET" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("leaders", "read");
    const [row] = await getDb()
      .select()
      .from(schema.leaders)
      .where(eq(schema.leaders.id, data.id))
      .limit(1);
    return row ?? null;
  });

export const createLeader = createServerFn({ method: "POST" })
  .inputValidator(leaderInput)
  .handler(async ({ data }) => {
    const user = await requirePermission("leaders", "write");
    const db = getDb();
    const slug = data.slug || (await generateUniqueSlug(schema.leaders, data.name));
    const portrait = await resolvePortraitMediaInput(data);
    const maxOrder = await db
      .select({ sortOrder: schema.leaders.sortOrder })
      .from(schema.leaders)
      .orderBy(asc(schema.leaders.sortOrder));
    const nextOrder = maxOrder.length ? Math.max(...maxOrder.map((r) => r.sortOrder)) + 1 : 0;

    const [row] = await db
      .insert(schema.leaders)
      .values({
        name: data.name,
        slug,
        navLabel: data.navLabel,
        designation: data.designation,
        qualifications: data.qualifications,
        memberships: data.memberships ?? [],
        bio: data.bio ?? [],
        initials: data.initials,
        portraitMediaId: portrait.portraitMediaId,
        portraitUrl: portrait.portraitUrl,
        sortOrder: data.sortOrder ?? nextOrder,
        status: data.status,
        publishedAt: data.status === "published" ? new Date() : null,
        createdBy: user.id,
        updatedBy: user.id,
      })
      .returning();
    await invalidateLeadersCache();
    return row;
  });

export const updateLeader = createServerFn({ method: "POST" })
  .inputValidator(leaderInput.extend({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    const user = await requirePermission("leaders", "write");
    const db = getDb();
    const [existing] = await db
      .select()
      .from(schema.leaders)
      .where(eq(schema.leaders.id, data.id))
      .limit(1);
    if (!existing) throw new Error("Not found");

    const slug = data.slug || existing.slug;
    const portrait = await resolvePortraitMediaInput(data);

    const [row] = await db
      .update(schema.leaders)
      .set({
        name: data.name,
        slug,
        navLabel: data.navLabel,
        designation: data.designation,
        qualifications: data.qualifications,
        memberships: data.memberships ?? existing.memberships,
        bio: data.bio ?? existing.bio,
        initials: data.initials ?? existing.initials,
        portraitMediaId: portrait.portraitMediaId,
        portraitUrl: portrait.portraitUrl,
        sortOrder: data.sortOrder ?? existing.sortOrder,
        status: data.status,
        publishedAt:
          data.status === "published" ? (existing.publishedAt ?? new Date()) : existing.publishedAt,
        updatedBy: user.id,
        updatedAt: new Date(),
      })
      .where(eq(schema.leaders.id, data.id))
      .returning();
    await invalidateLeadersCache(existing.slug === slug ? slug : [existing.slug, slug]);
    return row;
  });

export const deleteLeader = createServerFn({ method: "POST" })
  .inputValidator(z.object({ id: z.string().uuid() }))
  .handler(async ({ data }) => {
    await requirePermission("leaders", "delete");
    const [existing] = await getDb()
      .select({ slug: schema.leaders.slug })
      .from(schema.leaders)
      .where(eq(schema.leaders.id, data.id))
      .limit(1);
    await getDb().delete(schema.leaders).where(eq(schema.leaders.id, data.id));
    await invalidateLeadersCache(existing?.slug);
    return { ok: true };
  });

export const reorderLeaders = createServerFn({ method: "POST" })
  .inputValidator(z.object({ ids: z.array(z.string().uuid()).min(1) }))
  .handler(async ({ data }) => {
    await requirePermission("leaders", "write");
    const db = getDb();
    for (let i = 0; i < data.ids.length; i++) {
      await db
        .update(schema.leaders)
        .set({ sortOrder: i, updatedAt: new Date() })
        .where(and(eq(schema.leaders.id, data.ids[i]), inArray(schema.leaders.id, data.ids)));
    }
    await invalidateLeadersCache();
    return { ok: true };
  });

export { getPublishedLeaders as listPublishedLeaders, getLeaderBySlug as getLeaderBySlugPublic } from "./public.functions";

export const generateLeaderSlug = createServerFn({ method: "POST" })
  .inputValidator(z.object({ name: z.string().min(1), excludeId: z.string().uuid().optional() }))
  .handler(async ({ data }) => {
    await requirePermission("leaders", "write");
    return generateUniqueSlug(schema.leaders, data.name, data.excludeId);
  });
